import { Device } from "@fairfleet/geotab";
import { useMemo, useState } from "react";

/** Vehicles loaded at first and added per click on "load more" without a vehicle filter. */
export const VEHICLES_PER_LOAD = 5;
/** Step when more vehicles than this are picked in the filter. */
export const VEHICLES_PER_LOAD_MANY_PICKED = 20;
/** Up to this many picked vehicles all of them are loaded at once. */
export const VEHICLES_LOADED_COMPLETELY = 5;

/**
 * Step of "load more" for the number of vehicles picked in the filter: 5 without a filter, no
 * limit for up to five vehicles, 20 for more.
 */
export function loadStepForVehicles(selectedVehicles: number): number | null {
  if (selectedVehicles === 0) return VEHICLES_PER_LOAD;
  return selectedVehicles > VEHICLES_LOADED_COMPLETELY ? VEHICLES_PER_LOAD_MANY_PICKED : null;
}

const byName = (a: Device, b: Device) => (a.name ?? "").localeCompare(b.name ?? "", "de");

/**
 * The vehicles whose data the page loads: the first ones by name (the table's order), growing in
 * steps of `step` on request; `null` loads every vehicle. Trips, consumption and fuel are fetched
 * per vehicle, so a database with thousands of vehicles must not load them all at once. The
 * limit starts over whenever the vehicle list changes, i.e. after a group or vehicle filter change.
 */
export function useLoadedVehicles(devices: Device[], step: number | null) {
  const sorted = useMemo(() => [...devices].sort(byName), [devices]);
  // By id, not by array: a refetch of devices or groups hands over a new array of the same vehicles.
  const listKey = useMemo(() => sorted.map(device => device.id).join(","), [sorted]);
  const [loaded, setLoaded] = useState({ listKey, step, limit: step });
  // Compared during render, not reset in an effect: one render with the old limit would already
  // start the queries for that many vehicles of the new list.
  const limit = loaded.listKey === listKey && loaded.step === step ? loaded.limit : step;

  const loadedDevices = useMemo(
    () => (limit == null || sorted.length <= limit ? sorted : sorted.slice(0, limit)),
    [sorted, limit],
  );

  const loadMore = () => {
    if (step != null) setLoaded({ listKey, step, limit: (limit ?? 0) + step });
  };

  return {
    loadedDevices,
    shown: loadedDevices.length,
    total: sorted.length,
    canLoadMore: loadedDevices.length < sorted.length,
    /** Vehicles the next click adds; 0 when every vehicle is loaded. */
    nextStep: Math.min(step ?? 0, sorted.length - loadedDevices.length),
    loadMore,
  };
}
